import { useState, useEffect } from 'react';
import { Users, UserPlus, Loader2, Shield, ShieldOff, AlertCircle, Mail, Trash2, CheckCircle2, Pencil } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import ConfirmDialog from './ConfirmDialog';

interface OrgUser {
  id: string;
  email: string;
  full_name: string;
  role: 'member' | 'sensor';
  status: string;
  is_member_admin: boolean;
  is_sensor: boolean;
  is_primary_contact: boolean;
  email_verified: boolean;
  approved_at: string | null;
  created_at: string;
}

interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  created_at: string;
}

export default function MemberOrgUserManagement({ memberId }: { memberId: string }) {
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [pending, setPending] = useState<PendingInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Invite form
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'sensor'>('member');
  const [inviteAsAdmin, setInviteAsAdmin] = useState(false);
  const [inviting, setInviting] = useState(false);

  // Bekreftelses-dialog
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel?: string;
    variant?: 'default' | 'warning' | 'danger' | 'high_stakes';
    consequences?: string[];
    requireTypedConfirmation?: string | null;
    onConfirm: () => void | Promise<void>;
  } | null>(null);

  // v6.6.2 (task #47) — Rediger navn/e-post
  const [editingUser, setEditingUser] = useState<OrgUser | null>(null);
  const [editFullName, setEditFullName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const openEdit = (u: OrgUser) => {
    setEditingUser(u);
    setEditFullName(u.full_name || '');
    setEditEmail(u.email || '');
    setProfileError(null);
  };
  const closeEdit = () => {
    setEditingUser(null);
    setProfileError(null);
  };
  const saveProfile = async () => {
    if (!editingUser) return;
    setSavingProfile(true);
    setProfileError(null);
    try {
      const body: Record<string, string> = {};
      if (editFullName.trim() !== (editingUser.full_name || '')) body.full_name = editFullName.trim();
      if (editEmail.trim().toLowerCase() !== (editingUser.email || '').toLowerCase()) body.email = editEmail.trim().toLowerCase();
      if (Object.keys(body).length === 0) {
        closeEdit();
        return;
      }
      const res = await authenticatedFetch(`/api/admin/users/${editingUser.id}/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) {
        setProfileError(data.message || data.error || 'Kunne ikke lagre endring');
        return;
      }
      setSuccess(data.message || 'Endring lagret.');
      closeEdit();
      await loadUsers();
    } catch (e) {
      setProfileError((e as Error).message);
    } finally {
      setSavingProfile(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [memberId]);

  async function loadUsers() {
    setLoading(true);
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/members/${memberId}/users`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Kunne ikke laste brukere');
      setUsers(d.users || []);
      setPending(d.pending_invitations || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviting(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await authenticatedFetch(`/api/admin/members/${memberId}/users/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: inviteEmail,
          role: inviteRole,
          is_member_admin: inviteRole === 'member' ? inviteAsAdmin : false
        })
      });
      const d = await r.json();
      if (!r.ok) {
        // Spesielt: hvis domene ikke godkjent, vis godkjente domener
        if (d.approved_domains?.length > 0) {
          setError(`${d.error}\nGodkjente domener: ${d.approved_domains.join(', ')}`);
        } else {
          setError(d.error || 'Kunne ikke invitere');
        }
        return;
      }
      setSuccess(`Invitasjon sendt til ${inviteEmail}${inviteAsAdmin && inviteRole === 'member' ? ' (med administrator-rettigheter)' : ''}`);
      setInviteEmail('');
      setInviteAsAdmin(false);
      setShowInvite(false);
      await loadUsers();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setInviting(false);
    }
  }

  function askChangeStatus(user: OrgUser, newStatus: 'active' | 'inactive') {
    const isDeactivate = newStatus === 'inactive';
    setConfirm({
      title: newStatus === 'active' ? 'Aktiver bruker?' : 'Deaktiver bruker?',
      message: newStatus === 'active'
        ? `${user.full_name || user.email} vil få tilgang til å logge inn igjen.`
        : `Du er i ferd med å deaktivere ${user.full_name || user.email} (${user.email}).\n\nDette er en permanent handling for brukerens konto i organisasjonen.`,
      confirmLabel: newStatus === 'active' ? 'Aktiver' : 'Ja, deaktiver bruker',
      variant: newStatus === 'active' ? 'default' : 'high_stakes',
      requireTypedConfirmation: isDeactivate ? 'DEAKTIVER' : null,
      consequences: isDeactivate ? [
        'Brukeren mister umiddelbart tilgang til systemet — pålogging blokkeres.',
        'Brukerens historikk, signeringer og handlinger beholdes uendret i audit-log for sporing.',
        'Hvis brukeren har signert kvalitetssikringskontrakten eller DPA på vegne av organisasjonen, består signaturen (den er juridisk bindende for organisasjonen, ikke avhengig av at personen fortsatt er aktiv bruker).',
        'E-postadressen frigjøres og kan senere brukes til invitasjon av samme eller annen person — eventuelt i annen organisasjon.',
        'Handlingen kan reverseres senere ved å aktivere brukeren igjen.'
      ] : undefined,
      onConfirm: async () => {
        setError(null);
        try {
          const r = await authenticatedFetch(`/api/admin/users/${user.id}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: newStatus })
          });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error);
          await loadUsers();
        } catch (e: any) {
          setError(e.message);
        }
      }
    });
  }

  function askToggleSensor(user: OrgUser) {
    const currentlySensor = user.is_sensor || user.role === 'sensor';
    const action = currentlySensor ? 'revoke-sensor' : 'grant-sensor';
    setConfirm({
      title: currentlySensor ? 'Fjern sensor-tilgang?' : 'Gi sensor-tilgang?',
      message: currentlySensor
        ? `Fjerner sensor-tilgang fra ${user.full_name || user.email}. Brukeren beholder sin ${user.role === 'sensor' ? 'medlems-' : 'primær-'}rolle og all annen tilgang.`
        : `${user.full_name || user.email} vil kunne opptre som sensor for kurs, i tillegg til sin nåværende rolle.`,
      confirmLabel: currentlySensor ? 'Fjern sensor' : 'Gi sensor-tilgang',
      variant: currentlySensor ? 'warning' : 'default',
      onConfirm: async () => {
        setError(null);
        try {
          const r = await authenticatedFetch(`/api/admin/users/${user.id}/${action}`, { method: 'POST' });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || 'Kunne ikke endre sensor-tilgang');
          await loadUsers();
        } catch (e: any) {
          setError(e.message);
        }
      }
    });
  }

  function askTogglePromote(user: OrgUser) {
    const action = user.is_member_admin ? 'demote-member-admin' : 'promote-member-admin';
    setConfirm({
      title: user.is_member_admin ? 'Fjern administrator-rolle?' : 'Gi administrator-rettigheter?',
      message: user.is_member_admin
        ? `${user.full_name || user.email} vil ikke lenger kunne administrere andre brukere eller foreslå e-postdomener for organisasjonen.`
        : `${user.full_name || user.email} vil kunne se og administrere andre brukere + foreslå nye e-postdomener for organisasjonen.`,
      confirmLabel: user.is_member_admin ? 'Fjern administrator' : 'Gi administrator',
      variant: user.is_member_admin ? 'warning' : 'default',
      onConfirm: async () => {
        setError(null);
        try {
          const r = await authenticatedFetch(`/api/admin/users/${user.id}/${action}`, { method: 'POST' });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error);
          await loadUsers();
        } catch (e: any) {
          setError(e.message);
        }
      }
    });
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Users size={22} />
            Brukere i organisasjonen
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Administrer brukere som har tilgang til organisasjonen din. Inviter nye, fjern brukere som har sluttet, og utpek flere administratorer.
          </p>
        </div>
        {!showInvite && (
          <button
            onClick={() => setShowInvite(true)}
            className="flex items-center gap-2 bg-kf-primary text-white px-4 py-2 rounded-lg hover:bg-kf-primary-dark transition"
          >
            <UserPlus size={18} />
            Inviter bruker
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 m-0 whitespace-pre-line">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800 m-0">{success}</p>
        </div>
      )}

      {/* Invite form */}
      {showInvite && (
        <form onSubmit={handleInvite} className="border border-kf-blue-200 bg-kf-blue-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-kf-blue-900 mb-3 flex items-center gap-2">
            <Mail size={18} /> Inviter ny bruker
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">E-postadresse</label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="navn@dittfirma.no"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-600 mt-1">
                E-posten må tilhøre et godkjent domene. Sensorer kan ha ekstern e-post.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rolle</label>
              <select
                value={inviteRole}
                onChange={(e) => { setInviteRole(e.target.value as 'member' | 'sensor'); if (e.target.value === 'sensor') setInviteAsAdmin(false); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="member">Medlem</option>
                <option value="sensor">Sensor</option>
              </select>
            </div>
          </div>

          {/* Administrator-checkbox kun for medlem-rolle */}
          {inviteRole === 'member' && (
            <label className="flex items-start gap-2 mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={inviteAsAdmin}
                onChange={(e) => setInviteAsAdmin(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-sm">
                <strong className="text-purple-900">Gi administrator-rettigheter</strong>
                <p className="text-purple-800 m-0 mt-0.5 text-xs">
                  Denne brukeren vil kunne se og administrere andre brukere + foreslå nye e-postdomener for organisasjonen.
                </p>
              </span>
            </label>
          )}

          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={() => { setShowInvite(false); setInviteEmail(''); setError(null); }}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Avbryt
            </button>
            <button
              type="submit"
              disabled={inviting || !inviteEmail}
              className="px-4 py-2 bg-kf-primary text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
            >
              {inviting && <Loader2 className="w-4 h-4 animate-spin" />}
              Send invitasjon
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : (
        <>
          {/* Aktive brukere */}
          {users.length === 0 ? (
            <div className="text-center py-8 text-gray-500 border border-dashed rounded-lg">
              <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Ingen brukere registrert ennå.</p>
              <p className="text-sm">Inviter den første brukeren for å komme i gang.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id} className="border border-gray-200 rounded-lg p-3 flex items-start justify-between gap-3 bg-white">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <strong className="text-gray-900">{u.full_name || u.email}</strong>
                      <span className="text-sm text-gray-500">{u.email}</span>
                      {/* Primærrolle */}
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        u.role === 'sensor' ? 'bg-kf-blue-100 text-kf-blue-800' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {u.role === 'sensor' ? 'Sensor' : 'Medlem'}
                      </span>
                      {/* Administrator-flagg (uavhengig av sensor) */}
                      {u.is_member_admin && (
                        <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full flex items-center gap-1">
                          <Shield size={10} /> Administrator
                        </span>
                      )}
                      {/* Ekstra sensor-tilgang for admin/medlem */}
                      {u.role !== 'sensor' && u.is_sensor && (
                        <span className="px-2 py-0.5 text-xs bg-kf-blue-100 text-kf-blue-800 rounded-full">
                          + Sensor
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        u.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {u.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* v6.6.2 task #47: Rediger navn/e-post */}
                    <button
                      onClick={() => openEdit(u)}
                      title="Rediger navn og e-post"
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                    >
                      <Pencil size={16} />
                    </button>
                    {/* Sensor-toggle: legg til/fjern sensor-tilgang uten å endre primærrolle.
                        For rene sensorer (role='sensor') fjerner dette sensor-flagget og konverterer
                        til medlem — brukeren beholder tilgang. */}
                    <button
                      onClick={() => askToggleSensor(u)}
                      title={(u.is_sensor || u.role === 'sensor') ? 'Fjern sensor-tilgang' : 'Gi sensor-tilgang'}
                      className={`p-2 rounded text-xs ${
                        (u.is_sensor || u.role === 'sensor')
                          ? 'text-kf-blue-600 hover:bg-kf-blue-50'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      {(u.is_sensor || u.role === 'sensor') ? 'Fjern sensor' : '+ Sensor'}
                    </button>
                    {/* Promote/demote admin — kun for medlem-rolle (ikke rene sensorer) */}
                    {u.role === 'member' && (
                      <button
                        onClick={() => askTogglePromote(u)}
                        title={u.is_member_admin ? 'Fjern administrator' : 'Gjør til administrator'}
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                      >
                        {u.is_member_admin ? <ShieldOff size={16} /> : <Shield size={16} />}
                      </button>
                    )}
                    {/* Aktiver/deaktiver */}
                    {u.status === 'active' ? (
                      <button
                        onClick={() => askChangeStatus(u, 'inactive')}
                        title="Deaktiver"
                        className="p-2 text-amber-600 hover:bg-amber-50 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    ) : (
                      <button
                        onClick={() => askChangeStatus(u, 'active')}
                        title="Aktiver"
                        className="p-2 text-green-600 hover:bg-green-50 rounded"
                      >
                        <CheckCircle2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Ventende invitasjoner */}
          {pending.length > 0 && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="font-semibold text-gray-900 mb-3">Ventende invitasjoner ({pending.length})</h3>
              <div className="space-y-2">
                {pending.map((p) => (
                  <div key={p.id} className="border border-yellow-200 bg-yellow-50 rounded p-3 flex items-center justify-between">
                    <div>
                      <strong className="text-sm">{p.email}</strong>
                      <span className="ml-2 text-xs text-gray-600">{p.role === 'sensor' ? 'Sensor' : 'Medlem'}</span>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Sendt {new Date(p.created_at).toLocaleDateString('nb-NO')} · Utløper {new Date(p.expires_at).toLocaleDateString('nb-NO')}
                      </div>
                    </div>
                    <span className="text-xs px-2 py-1 bg-yellow-200 text-yellow-900 rounded-full">Venter på aktivering</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Bekreftelses-dialog for alle destruktive/rolle-endrende handlinger */}
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        confirmLabel={confirm?.confirmLabel}
        variant={confirm?.variant}
        consequences={confirm?.consequences}
        requireTypedConfirmation={confirm?.requireTypedConfirmation}
        onConfirm={async () => {
          const action = confirm?.onConfirm;
          setConfirm(null);
          if (action) await action();
        }}
        onCancel={() => setConfirm(null)}
      />

      {/* v6.6.2 task #47: Rediger navn/e-post-modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Pencil className="w-4 h-4" /> Rediger navn og e-post
              </h3>
              <button onClick={closeEdit} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-6 space-y-4">
              {/* Hybrid-guard: samme regel som backend håndhever */}
              {editingUser.is_sensor && editingUser.role !== 'sensor' && (
                <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm text-purple-900">
                  <strong>Hybrid-bruker:</strong> Denne brukeren har både primærrolle og sensor-rolle.
                  Kontoinfo (navn og e-post) er felles med sensor-siden og kan derfor ikke endres herfra
                  uten at sensor-rollen fjernes først. Fjern sensor-tilgang, eller be systemadmin gjøre endringen.
                </div>
              )}

              {profileError && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5" />
                  <span className="whitespace-pre-line">{profileError}</span>
                </div>
              )}

              <label className="block">
                <span className="text-sm font-medium text-gray-700">Fullt navn</span>
                <input
                  type="text"
                  value={editFullName}
                  onChange={e => setEditFullName(e.target.value)}
                  disabled={savingProfile || (editingUser.is_sensor && editingUser.role !== 'sensor')}
                  className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Nytt navn vises umiddelbart i sensur-lister og sensor-tildelinger. Historiske attester
                  bevarer navn ved utstedelses-tidspunkt.
                </p>
              </label>

              <label className="block">
                <span className="text-sm font-medium text-gray-700">E-postadresse</span>
                <input
                  type="email"
                  value={editEmail}
                  onChange={e => setEditEmail(e.target.value)}
                  disabled={savingProfile || (editingUser.is_sensor && editingUser.role !== 'sensor')}
                  className="mt-1 block w-full rounded border-gray-300 px-3 py-2 text-sm disabled:bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ny e-post må tilhøre et godkjent domene for organisasjonen (unntak for sensor-rolle).
                  Endring avslutter alle aktive innlogginger — brukeren må logge inn på nytt.
                </p>
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={closeEdit}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                  disabled={savingProfile}
                >
                  Avbryt
                </button>
                <button
                  onClick={saveProfile}
                  disabled={savingProfile || (editingUser.is_sensor && editingUser.role !== 'sensor')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-kf-primary text-white rounded text-sm font-medium disabled:opacity-50"
                >
                  {savingProfile && <Loader2 className="w-4 h-4 animate-spin" />}
                  Lagre endring
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
